const leafletScriptUrl = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
const leafletStyleUrl = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';

const leafletPromise = (() => {
  if (window.L) return Promise.resolve(window.L);
  if (window.__leafletPromise) return window.__leafletPromise;

  window.__leafletPromise = new Promise((resolve, reject) => {
    const existingScript = document.getElementById('leaflet-script');
    const existingStyle = document.getElementById('leaflet-style');

    if (!existingStyle) {
      const link = document.createElement('link');
      link.id = 'leaflet-style';
      link.rel = 'stylesheet';
      link.href = leafletStyleUrl;
      document.head.appendChild(link);
    }

    const handleLoad = () => resolve(window.L);
    const handleError = () =>
      reject(new Error('Leaflet failed to load. Please refresh the page.'));

    if (existingScript) {
      if (window.L) {
        resolve(window.L);
        return;
      }
      existingScript.addEventListener('load', handleLoad, { once: true });
      existingScript.addEventListener('error', handleError, { once: true });
      return;
    }

    const script = document.createElement('script');
    script.id = 'leaflet-script';
    script.src = leafletScriptUrl;
    script.async = true;
    script.onload = handleLoad;
    script.onerror = handleError;
    document.head.appendChild(script);
  });

  return window.__leafletPromise;
})();

const markerClusterScriptUrl =
  'https://unpkg.com/leaflet.markercluster@1.5.3/dist/leaflet.markercluster.js';
const markerClusterStyleUrl =
  'https://unpkg.com/leaflet.markercluster@1.5.3/dist/MarkerCluster.css';

const markerClusterPromise = (async () => {
  const L = await leafletPromise;
  if (L && L.MarkerClusterGroup) return L;
  await appendStyleOnce('leaflet-marker-cluster-style', markerClusterStyleUrl);
  await appendScriptOnce('leaflet-marker-cluster-script', markerClusterScriptUrl);
  return L;
})();

const mapBlocks = Array.from(document.querySelectorAll('[data-retailers-map]'));
if (mapBlocks.length) {
  ensureMarkerStyles();
  mapBlocks.forEach((block) => initializeMap(block));
}

async function initializeMap(block) {
  const mapContainerId = block.dataset.mapId;
  const mapContainer = document.getElementById(mapContainerId);
  const emptyMessage = block.querySelector('[data-map-empty]');
  const filterSelect = block.querySelector('[data-retailer-filter]');

  if (!mapContainer) {
    return;
  }

  try {
    await markerClusterPromise;
  } catch (error) {
    showMessage(emptyMessage, 'Unable to load map library.');
    console.error(error);
    return;
  }

  if (!window.L) {
    showMessage(emptyMessage, 'Map library unavailable.');
    return;
  }

  const map = L.map(mapContainer, {
    scrollWheelZoom: false,
    worldCopyJump: true,
  }).setView(
    [parseFloat(block.dataset.defaultLat), parseFloat(block.dataset.defaultLng)],
    parseInt(block.dataset.defaultZoom, 10)
  );
  enableScrollZoomWithModifier(map);

  const tileUrl =
    block.dataset.tileUrl ||
    'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
  const tileAttribution =
    block.dataset.tileAttribution ||
    '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';

  L.tileLayer('https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png', {
    attribution: tileAttribution,
    maxZoom: 19,
  }).addTo(map);
   

  const dataUrl = block.dataset.mapSrc;
  let features = [];

  try {
    const response = await fetch(dataUrl);
    const data = await response.json();
    features = flattenFeatures(data);
  } catch (error) {
    showMessage(emptyMessage, 'Unable to load map data.');
    console.error(error);
    return;
  }

  const { markersByRetailer, missingCoords } = buildMarkerGroups(features);
  const allMarkers = Array.from(markersByRetailer.values()).flat();

  if (markersByRetailer.size === 0) {
    const fallback =
      missingCoords > 0
        ? 'No coordinates found in retailers-map.json. Add lat/lng values to render markers.'
        : 'No locations available to display.';
    showMessage(emptyMessage, fallback);
    return;
  }

  const clusterGroups = new Map();
  markersByRetailer.forEach((markers, retailer) => {
    const brandColor = getBrandColor(retailer);
    const cluster = L.markerClusterGroup({
      spiderfyOnMaxZoom: true,
      showCoverageOnHover: false,
      removeOutsideVisibleBounds: true,
      iconCreateFunction: (c) => {
        const count = c.getChildCount();
        return L.divIcon({
          html: `
            <div class="retailer-cluster" style="border-color:${brandColor}; background:${brandColor}22;">
              <div class="retailer-cluster__inner" style="background:${brandColor}; border-color:${brandColor}; color:#fff;">${count}</div>
            </div>
          `,
          className: 'retailer-cluster-wrapper',
          iconSize: [40, 40],
        });
      },
    });
    cluster.addLayers(markers);
    clusterGroups.set(retailer, cluster);
  });

  const addVisible = (retailerFilter) => {
    // Remove all clusters first
    clusterGroups.forEach((cluster) => {
      map.removeLayer(cluster);
    });

    const visibleClusters = [];
    clusterGroups.forEach((cluster, retailer) => {
      if (retailerFilter === 'all' || retailerFilter === retailer) {
        cluster.addTo(map);
        visibleClusters.push(cluster);
      }
    });

    fitToVisible(map, visibleClusters);
  };

  addVisible('all');
  renderLegend(block, markersByRetailer, addVisible, filterSelect);

  if (filterSelect) {
    populateFilter(filterSelect, markersByRetailer.keys());
    filterSelect.addEventListener('change', () => {
      addVisible(filterSelect.value);
      setLegendActive(block, filterSelect.value);
    });
  }

  const locateButton = block.querySelector('[data-map-locate]');
  if (locateButton) {
    locateButton.addEventListener('click', () =>
      locateNearest(locateButton, map, allMarkers)
    );
  }
}

function flattenFeatures(data) {
  if (!data || typeof data !== 'object') return [];

  return Object.entries(data).flatMap(([retailer, items]) =>
    (items || []).map((feature) => ({
      ...feature,
      retailer,
    }))
  );
}

function buildMarkerGroups(features) {
  const markersByRetailer = new Map();
  let missingCoords = 0;

  features.forEach((feature) => {
    const coords = getCoordinates(feature);
    if (!coords) {
      missingCoords += 1;
      return;
    }

    const retailer = feature.retailer || feature?.properties?.name || 'Retailer';
    const marker = L.marker(coords, {
      icon: getMarkerIcon(retailer),
    }).bindPopup(buildPopup(feature));

    const arr =
      markersByRetailer.get(retailer) ||
      markersByRetailer.set(retailer, []).get(retailer);
    arr.push(marker);
  });

  return { markersByRetailer, missingCoords };
}

function getCoordinates(feature) {
  if (!feature) return null;

  const geometry = feature.geometry;
  if (
    geometry &&
    geometry.type === 'Point' &&
    Array.isArray(geometry.coordinates) &&
    geometry.coordinates.length >= 2
  ) {
    const [lng, lat] = geometry.coordinates;
    if (isValidCoordinate(lat, -90, 90) && isValidCoordinate(lng, -180, 180)) {
      return [lat, lng];
    }
  }

  const props = feature.properties || {};
  const latCandidate =
    props.latitude || props.lat || props.Latitude || props.Lat || null;
  const lngCandidate =
    props.longitude || props.lng || props.lon || props.Longitude || props.Lng || null;

  const lat = parseFloat(latCandidate);
  const lng = parseFloat(lngCandidate);

  if (isValidCoordinate(lat, -90, 90) && isValidCoordinate(lng, -180, 180)) {
    return [lat, lng];
  }

  return null;
}

function isValidCoordinate(value, min, max) {
  return Number.isFinite(value) && value >= min && value <= max;
}

function buildPopup(feature) {
  const props = feature.properties || {};
  const name = props.name || feature.retailer || 'Retailer';
  const address =
    props.address ||
    [
      props['Address'],
      props['Store Address Line 1'],
      props.City,
      props.Town,
      props.State || props.Province,
      props['Postal Code'] || props['Store Postal Code'] || props.Zip,
    ]
      .filter(Boolean)
      .join(', ');

  const details = document.createElement('div');
  const title = document.createElement('strong');
  title.textContent = name;
  details.appendChild(title);

  if (address) {
    const addressEl = document.createElement('div');
    addressEl.textContent = address;
    details.appendChild(addressEl);
  }

  if (props['Telephone#']) {
    const phoneEl = document.createElement('div');
    phoneEl.textContent = props['Telephone#'];
    details.appendChild(phoneEl);
  }

  if (props.description) {
    const desc = document.createElement('div');
    desc.innerHTML = props.description;
    details.appendChild(desc);
  }

  return details;
}

function populateFilter(select, retailers) {
  const uniqueRetailers = Array.from(new Set(retailers));
  uniqueRetailers.sort((a, b) => a.localeCompare(b));

  uniqueRetailers.forEach((retailer) => {
    const option = document.createElement('option');
    option.value = retailer;
    option.textContent = retailer;
    select.appendChild(option);
  });
}

function applyFilter(selectedValue, map, groups, allGroups) {
  allGroups.forEach((group) => map.removeLayer(group));

  const visible = [];

  groups.forEach((group, retailer) => {
    if (selectedValue === 'all' || selectedValue === retailer) {
      group.addTo(map);
      visible.push(group);
    }
  });

  fitToVisible(map, visible);
}

function fitToVisible(map, markerGroups) {
  const bounds = markerGroups.reduce((acc, group) => {
    if (!group || !group.getBounds) return acc;
    const groupBounds = group.getBounds();
    if (!groupBounds || !groupBounds.isValid()) return acc;
    if (!acc) return groupBounds;
    return acc.extend(groupBounds);
  }, null);

  if (bounds && bounds.isValid()) {
    map.fitBounds(bounds, { padding: [20, 20], maxZoom: 12 });
  }
}

function showMessage(target, message) {
  if (!target) return;
  target.hidden = false;
  target.textContent = message;
}

function getMarkerIcon(retailer) {
  const color = getBrandColor(retailer);

  return L.divIcon({
    className: 'retailer-marker',
    html: `<span class="retailer-marker__dot" style="background:${color}"></span>`,
    iconSize: [20, 20],
    iconAnchor: [10, 20],
    popupAnchor: [0, -16],
  });
}

function getBrandColor(retailer) {
  const palette = {
    "lowe's": '#01579B', // Lowe's blue
    lowes: '#01579B',
    'home hardware': '#e31837', // strong red
    'home depot canada': '#F96302', // Home Depot orange
    'home depot usa': '#F96302',
    'canadian tire': '#C21807', // Canadian Tire red
  };

  return palette[retailer?.toLowerCase?.().trim?.() || ''] || '#2d68ff';
}

function ensureMarkerStyles() {
  if (document.getElementById('retailer-marker-style')) return;
  const style = document.createElement('style');
  style.id = 'retailer-marker-style';
  style.textContent = `
    .retailer-marker {
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .retailer-marker__dot {
      display: inline-block;
      width: 16px;
      height: 16px;
      border-radius: 999px;
      border: 2px solid #ffffff;
      box-shadow: 0 2px 6px rgba(0, 0, 0, 0.35);
    }
    .retailer-cluster-wrapper {
      background: transparent;
      border-radius: 50%;
      display: grid;
      place-items: center;
      color: #0f172a;
      font-weight: 700;
      box-shadow: 0 4px 10px rgba(0, 0, 0, 0.25);
    }
    .retailer-cluster {
      border-radius: 50%;
      display: grid;
      place-items: center;
      padding: 4px;
    }
    .retailer-cluster__inner {
      background: #fff;
      border-radius: 50%;
      min-width: 30px;
      min-height: 30px;
      display: grid;
      place-items: center;
      padding: 4px;
      border: 2px solid transparent;
    }
  `;
  document.head.appendChild(style);
}

function appendStyleOnce(id, href) {
  return new Promise((resolve, reject) => {
    if (document.getElementById(id)) return resolve();
    const link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    link.href = href;
    link.onload = resolve;
    link.onerror = () => reject(new Error(`Failed to load style ${href}`));
    document.head.appendChild(link);
  });
}

function appendScriptOnce(id, src) {
  return new Promise((resolve, reject) => {
    if (document.getElementById(id)) return resolve();
    const script = document.createElement('script');
    script.id = id;
    script.src = src;
    script.async = true;
    script.onload = resolve;
    script.onerror = () => reject(new Error(`Failed to load script ${src}`));
    document.head.appendChild(script);
  });
}

function enableScrollZoomWithModifier(map) {
  // Disable by default; allow when holding Ctrl/Cmd during scroll.
  map.scrollWheelZoom.disable();
  let disableTimeout = null;

  map.on('wheel', (e) => {
    const evt = e.originalEvent;
    const allow = evt && (evt.ctrlKey || evt.metaKey);
    if (!allow) {
      map.scrollWheelZoom.disable();
      return;
    }
    map.scrollWheelZoom.enable();
    if (disableTimeout) clearTimeout(disableTimeout);
    disableTimeout = setTimeout(() => {
      map.scrollWheelZoom.disable();
    }, 600);
  });
}

function renderLegend(block, markersByRetailer, addVisible, filterSelect) {
  const legend = block.querySelector('[data-map-legend]');
  if (!legend) return;

  const retailers = Array.from(markersByRetailer.keys());
  if (!retailers.length) {
    legend.hidden = true;
    return;
  }

  // Clear and repopulate
  legend.innerHTML = '';

  const selected = filterSelect?.value || 'all';

  retailers.sort((a, b) => a.localeCompare(b)).forEach((retailer) => {
    const color = getBrandColor(retailer);
    const item = document.createElement('div');
    item.className = 'retailer-map-legend__item';
    item.dataset.retailer = retailer;
    if (selected === retailer) {
      item.classList.add('retailer-map-legend__item--active');
    }
    item.innerHTML = `
      <span class="retailer-map-legend__swatch" style="background:${color};"></span>
      <span>${retailer}</span>
    `;
    item.addEventListener('click', () => {
      const target = legend.querySelector('.retailer-map-legend__item--active');
      if (target) target.classList.remove('retailer-map-legend__item--active');
      item.classList.add('retailer-map-legend__item--active');
      addVisible(retailer);
      if (filterSelect) {
        filterSelect.value = retailer;
      }
      setLegendActive(block, retailer);
    });
    legend.appendChild(item);
  });

  // Add "All" control
  const allItem = document.createElement('div');
  allItem.className = 'retailer-map-legend__item';
  allItem.dataset.retailer = 'all';
  if (selected === 'all') {
    allItem.classList.add('retailer-map-legend__item--active');
  }
  allItem.innerHTML = `
    <span class="retailer-map-legend__swatch" style="background:#222;"></span>
    <span>All</span>
  `;
  allItem.addEventListener('click', () => {
    const target = legend.querySelector('.retailer-map-legend__item--active');
    if (target) target.classList.remove('retailer-map-legend__item--active');
    allItem.classList.add('retailer-map-legend__item--active');
    addVisible('all');
    if (filterSelect) {
      filterSelect.value = 'all';
    }
    setLegendActive(block, 'all');
  });
  legend.insertBefore(allItem, legend.firstChild);

  legend.hidden = false;
}

function setLegendActive(block, retailer) {
  const legend = block.querySelector('[data-map-legend]');
  if (!legend) return;
  const current = legend.querySelector('.retailer-map-legend__item--active');
  if (current) current.classList.remove('retailer-map-legend__item--active');
  const next = legend.querySelector(`[data-retailer="${CSS.escape(retailer)}"]`);
  if (next) {
    next.classList.add('retailer-map-legend__item--active');
  }
}

function locateNearest(button, map, markers) {
  if (!markers || !markers.length) {
    alert('No locations to locate.');
    return;
  }

  if (!navigator.geolocation) {
    alert('Geolocation is not supported in this browser.');
    return;
  }

  const originalText = button.textContent;
  button.disabled = true;
  button.textContent = 'Locating...';

  navigator.geolocation.getCurrentPosition(
    (pos) => {
      const userLatLng = L.latLng(pos.coords.latitude, pos.coords.longitude);
      let nearest = null;
      let nearestDistance = Infinity;

      markers.forEach((marker) => {
        const dist = map.distance(userLatLng, marker.getLatLng());
        if (dist < nearestDistance) {
          nearestDistance = dist;
          nearest = marker;
        }
      });

      if (nearest) {
        if (map.__userLocationLayer) {
          map.removeLayer(map.__userLocationLayer);
        }

        const accuracyRadius = Math.max(200, pos.coords.accuracy || 0);
        const userLayer = L.layerGroup();
        const userDot = L.circleMarker(userLatLng, {
          radius: 6,
          color: '#2563eb',
          weight: 2,
          fillColor: '#60a5fa',
          fillOpacity: 0.85,
        });
        const userAccuracy = L.circle(userLatLng, {
          radius: accuracyRadius,
          color: '#2563eb',
          weight: 1,
          fillOpacity: 0.08,
        });
        userLayer.addLayer(userAccuracy).addLayer(userDot).addTo(map);
        map.__userLocationLayer = userLayer;

        const bounds = L.latLngBounds([userLatLng, nearest.getLatLng()]);
        const padded = bounds.isValid() ? bounds.pad(0.25) : userLatLng.toBounds(40000); // ~20-25km
        map.fitBounds(padded, { maxZoom: 14, padding: [20, 20] });
        nearest.openPopup();
      } else {
        alert('No nearby locations found.');
      }

      button.disabled = false;
      button.textContent = originalText;
    },
    (error) => {
      console.error(error);
      alert('Unable to get your location. Please check permissions.');
      button.disabled = false;
      button.textContent = originalText;
    },
    {
      enableHighAccuracy: false,
      maximumAge: 30000,
      timeout: 10000,
    }
  );
}
